import React, { useState, useEffect, useRef } from 'react';
import { vehicleRecordsApi, vehicleAlertsApi, unitsApi, reportsApi, aiApi, trailersApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Wrench,
  Plus,
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  File,
  Car,
  Shield,
  Calendar,
  Bell,
  ClipboardList,
  Upload,
  Mail,
  Download as DownloadIcon,
  Sparkles,
  Loader2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { DragDropZone } from '../components/DragDropZone';
import { formatDate, formatCurrency } from '../lib/utils';
import UnitSelectItems from '../components/UnitSelectItems';

const RECORD_TYPES = [
  { value: 'inspection', label: 'Inspection', icon: ClipboardList, color: 'text-blue-600' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'text-green-600' },
  { value: 'repair', label: 'Repair', icon: Car, color: 'text-orange-600' },
  { value: 'accident', label: 'Accident', icon: AlertTriangle, color: 'text-red-600' },
  { value: 'registration', label: 'Registration', icon: FileText, color: 'text-purple-600' },
  { value: 'insurance', label: 'Insurance', icon: Shield, color: 'text-cyan-600' },
  { value: 'irp', label: 'IRP Registration', icon: FileText, color: 'text-indigo-600' },
  { value: 'ucr', label: 'UCR Filing', icon: FileText, color: 'text-pink-600' },
  { value: 'ifta', label: 'IFTA Permit', icon: FileText, color: 'text-teal-600' },
  { value: 'dot_inspection', label: 'DOT Inspection', icon: ClipboardList, color: 'text-amber-600' },
  { value: 'drug_test', label: 'Drug/Alcohol Test', icon: FileText, color: 'text-rose-600' },
  { value: 'other', label: 'Other Document', icon: FileText, color: 'text-slate-600' },
];

const ALERT_TYPES = [
  { value: 'inspection', label: 'Annual Inspection Due' },
  { value: 'maintenance', label: 'Preventative Maintenance' },
  { value: 'registration', label: 'Registration Expiry' },
  { value: 'insurance', label: 'Insurance Expiry' },
  { value: 'annual_dot_inspection', label: 'Annual DOT Inspection' },
  { value: 'quarterly_inspection', label: 'Quarterly Inspection' },
  { value: 'ifta_permit', label: 'IFTA Permit Renewal' },
  { value: 'irp_registration', label: 'IRP Registration' },
  { value: 'ucr_filing', label: 'UCR Filing' },
  { value: 'drug_consortium', label: 'Drug Consortium Renewal' },
  { value: 'other', label: 'Other Document/Reminder' },
];

export default function VehicleManagement() {
  const { canEdit, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('records');
  const [records, setRecords] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [units, setUnits] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editingAlert, setEditingAlert] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  // AI OCR state
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [extractingInfo, setExtractingInfo] = useState(false);
  const ocrInputRef = useRef(null);

  const [recordForm, setRecordForm] = useState({
    unit_id: '',
    trailer_id: '',
    record_type: 'maintenance',
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    cost: '',
    vendor: '',
    notes: '',
    file_ids: [],
    expiration_date: '',  // For documents that expire
    create_alert: false,  // Auto-create alert when document expires
    reminder_days: '30',  // Days before expiration to remind
  });

  const [alertForm, setAlertForm] = useState({
    unit_id: '',
    trailer_id: '',
    alert_type: 'inspection',
    title: '',
    due_date: '',
    notes: '',
    status: 'pending',
    reminder_days: '30', // Days before due date to send reminder
    send_reminder: true,
  });

  useEffect(() => {
    loadData();
  }, [selectedUnit]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [unitsRes, trailersRes, recordsRes, alertsRes] = await Promise.all([
        unitsApi.getAll(),
        trailersApi.getAll().catch(() => ({ data: [] })),
        vehicleRecordsApi.getAll(selectedUnit !== 'all' ? { unit_id: selectedUnit } : {}),
        vehicleAlertsApi.getAll(selectedUnit !== 'all' ? { unit_id: selectedUnit } : {}),
      ]);
      setUnits(unitsRes.data);
      setTrailers(trailersRes.data || []);
      setRecords(recordsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordSubmit = async (e) => {
    e.preventDefault();
    if (!recordForm.unit_id && !recordForm.trailer_id) {
      toast.error('Please select a Unit or a Trailer');
      return;
    }
    try {
      const data = {
        ...recordForm,
        unit_id: recordForm.unit_id || null,
        trailer_id: recordForm.trailer_id || null,
        cost: recordForm.cost ? parseFloat(recordForm.cost) : null,
        file_ids: uploadedFiles.map(f => f.file_id),
      };
      
      // Remove alert-specific fields from record data
      const { create_alert, reminder_days, expiration_date, ...recordData } = data;

      let savedRecord;
      if (editingRecord) {
        await vehicleRecordsApi.update(editingRecord.id, recordData);
        toast.success('Record updated successfully');
      } else {
        const res = await vehicleRecordsApi.create(recordData);
        savedRecord = res.data;
        toast.success('Record created successfully');
      }
      
      // Auto-create alert if expiration date is set and create_alert is checked
      if (!editingRecord && create_alert && expiration_date && recordForm.unit_id) {
        try {
          const alertTypeMap = {
            'registration': 'registration',
            'insurance': 'insurance',
            'irp': 'irp_registration',
            'ucr': 'ucr_filing',
            'ifta': 'ifta_permit',
            'dot_inspection': 'annual_dot_inspection',
            'drug_test': 'drug_consortium',
            'inspection': 'inspection',
            'other': 'other',
          };
          
          const alertData = {
            unit_id: recordForm.unit_id,
            alert_type: alertTypeMap[recordForm.record_type] || 'other',
            title: `${recordForm.title} - Expires`,
            due_date: expiration_date,
            notes: `Auto-created alert for ${recordForm.title}. Reminder ${reminder_days} days before expiration.`,
            status: 'pending',
            reminder_days: parseInt(reminder_days),
            send_reminder: true,
          };
          
          await vehicleAlertsApi.create(alertData);
          toast.success('Expiration alert created');
        } catch (alertError) {
          console.error('Failed to create alert:', alertError);
          toast.error('Record saved but alert creation failed');
        }
      }
      
      setRecordDialogOpen(false);
      resetRecordForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File must be smaller than 100MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await vehicleRecordsApi.uploadDocument(formData);
      setUploadedFiles(prev => [...prev, {
        file_id: res.data.file_id,
        filename: res.data.filename,
        size: res.data.size
      }]);
      toast.success('File uploaded');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeUploadedFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.file_id !== fileId));
  };

  // Handle AI OCR upload for vehicle documents
  const handleOCRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setExtractingInfo(true);
    try {
      const res = await aiApi.extractVehicleInfo(file);
      const extracted = res.data.extracted_data;
      
      if (extracted.error) {
        toast.error(extracted.error);
        return;
      }

      // Pre-fill the record form with extracted data
      setRecordForm(prev => ({
        ...prev,
        unit_id: extracted.unit_id || prev.unit_id,
        record_type: extracted.document_type || prev.record_type,
        title: extracted.title || prev.title,
        description: extracted.description || prev.description,
        date: extracted.date || prev.date,
        cost: extracted.cost?.toString() || prev.cost,
        vendor: extracted.vendor || prev.vendor,
        notes: extracted.notes || prev.notes,
        expiration_date: extracted.expiration_date || prev.expiration_date,
        create_alert: extracted.expiration_date ? true : false,
      }));

      // Add the uploaded file to the list
      setUploadedFiles([{
        file_id: res.data.file_id,
        filename: res.data.file_name,
        size: res.data.file_size
      }]);

      toast.success('Document analyzed! Please review the extracted information.');
      setOcrDialogOpen(false);
      setRecordDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract document information');
    } finally {
      setExtractingInfo(false);
      if (ocrInputRef.current) ocrInputRef.current.value = '';
    }
  };

  const handleAlertSubmit = async (e) => {
    e.preventDefault();
    if (!alertForm.unit_id && !alertForm.trailer_id) {
      toast.error('Please select a Unit or a Trailer');
      return;
    }
    const payload = {
      ...alertForm,
      unit_id: alertForm.unit_id || null,
      trailer_id: alertForm.trailer_id || null,
    };
    try {
      if (editingAlert) {
        await vehicleAlertsApi.update(editingAlert.id, payload);
        toast.success('Alert updated successfully');
      } else {
        await vehicleAlertsApi.create(payload);
        toast.success('Alert created successfully');
      }
      setAlertDialogOpen(false);
      resetAlertForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await vehicleRecordsApi.delete(id);
      toast.success('Record deleted');
      loadData();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleDeleteAlert = async (id) => {
    if (!window.confirm('Delete this alert?')) return;
    try {
      await vehicleAlertsApi.delete(id);
      toast.success('Alert deleted');
      loadData();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await vehicleAlertsApi.sendReminders();
      toast.success(`Reminders sent! ${res.data.overdue_count} overdue, ${res.data.upcoming_count} upcoming`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleDownloadReport = async () => {
    setGeneratingReport(true);
    try {
      const res = await reportsApi.vehicleCompliance();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Vehicle_Compliance_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const resetRecordForm = () => {
    setEditingRecord(null);
    setRecordForm({
      unit_id: '',
      trailer_id: '',
      record_type: 'maintenance',
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      cost: '',
      vendor: '',
      notes: '',
      file_ids: [],
      expiration_date: '',
      create_alert: false,
      reminder_days: '30',
    });
    setUploadedFiles([]);
  };

  const resetAlertForm = () => {
    setEditingAlert(null);
    setAlertForm({
      unit_id: '',
      trailer_id: '',
      alert_type: 'inspection',
      title: '',
      due_date: '',
      notes: '',
      status: 'pending',
      reminder_days: '30',
      send_reminder: true,
    });
  };

  const getUnitNumber = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit?.unit_number || 'Unknown';
  };

  const getTrailerNumber = (trailerId) => {
    const t = trailers.find(x => x.id === trailerId);
    return t?.trailer_number || '';
  };

  // Display label for a record/alert row — "Unit X" or "Trailer Y" depending on which is set.
  const getAssetLabel = (row) => {
    if (row?.trailer_id) return `Trailer ${getTrailerNumber(row.trailer_id) || row.trailer_id}`;
    if (row?.unit_id) return `Unit ${getUnitNumber(row.unit_id)}`;
    return '-';
  };

  const getRecordTypeInfo = (type) => RECORD_TYPES.find(r => r.value === type) || RECORD_TYPES[0];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3 mr-1" />Overdue</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  const upcomingAlerts = alerts.filter(a => a.status !== 'completed').slice(0, 5);
  const overdueAlerts = alerts.filter(a => a.status === 'overdue');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vehicle-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-manrope font-bold text-foreground">Vehicle Management</h1>
          <p className="text-muted-foreground mt-1">Track inspections, maintenance, repairs & compliance</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button 
                variant="outline" 
                onClick={handleSendReminders}
                disabled={sendingReminders}
                data-testid="send-reminders-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                {sendingReminders ? 'Sending...' : 'Email Reminders'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleDownloadReport}
                disabled={generatingReport}
                data-testid="download-report-btn"
              >
                <DownloadIcon className="h-4 w-4 mr-2" />
                {generatingReport ? 'Generating...' : 'Compliance Report'}
              </Button>
            </>
          )}
          <Select value={selectedUnit} onValueChange={setSelectedUnit}>
            <SelectTrigger className="w-[180px]" data-testid="unit-filter">
              <SelectValue placeholder="Filter by unit" />
            </SelectTrigger>
            <SelectContent>
              <UnitSelectItems units={units} includeAll allLabel="All Units" />
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Alert Summary Cards */}
      {overdueAlerts.length > 0 && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-800 dark:text-red-400">
                  {overdueAlerts.length} Overdue Alert{overdueAlerts.length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-600">Immediate attention required</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="records" data-testid="records-tab">
            <FileText className="h-4 w-4 mr-2" />
            Records
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="alerts-tab">
            <Bell className="h-4 w-4 mr-2" />
            Alerts & Reminders
          </TabsTrigger>
        </TabsList>

        {/* Records Tab */}
        <TabsContent value="records" className="space-y-4">
          <div className="flex justify-end gap-2">
            {canEdit && (
              <>
                {/* AI Upload Button */}
                <Dialog open={ocrDialogOpen} onOpenChange={setOcrDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50" data-testid="ai-upload-btn">
                      <Sparkles className="h-4 w-4 mr-2" />
                      AI Upload
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-blue-600" />
                        AI Document Upload
                      </DialogTitle>
                      <DialogDescription>
                        Upload a vehicle document (inspection, repair invoice, registration, etc.) and AI will extract the information automatically.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {extractingInfo ? (
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                            <p className="text-sm text-muted-foreground">Analyzing document...</p>
                          </div>
                        </div>
                      ) : (
                        <DragDropZone
                          onFileSelect={(file) => handleOCRUpload({ target: { files: [file] } })}
                          accept=".pdf"
                          data-testid="ocr-file-input"
                        >
                          <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                          <p className="text-sm font-medium">Drag & drop or click to upload vehicle document</p>
                          <p className="text-xs text-muted-foreground mt-1">PDF files only, up to 10MB</p>
                        </DragDropZone>
                      )}
                      <p className="text-xs text-muted-foreground">
                        AI will extract: Document type, title, date, cost, vendor, expiration date, and try to match to a unit
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Add Record Button */}
                <Dialog open={recordDialogOpen} onOpenChange={(open) => {
                  setRecordDialogOpen(open);
                  if (!open) resetRecordForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-record-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Record
                    </Button>
                  </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>{editingRecord ? 'Edit' : 'Add'} Vehicle Record</DialogTitle>
                    <DialogDescription>
                      Track inspections, maintenance, repairs and accidents
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleRecordSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Unit</Label>
                        <Select
                          value={recordForm.unit_id || 'none'}
                          onValueChange={(v) => setRecordForm({
                            ...recordForm,
                            unit_id: v === 'none' ? '' : v,
                            // picking a unit clears trailer so only one is set
                            trailer_id: v === 'none' ? recordForm.trailer_id : '',
                          })}
                        >
                          <SelectTrigger data-testid="record-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— None —</SelectItem>
                            <UnitSelectItems units={units} hideInactive />
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Trailer</Label>
                        <Select
                          value={recordForm.trailer_id || 'none'}
                          onValueChange={(v) => setRecordForm({
                            ...recordForm,
                            trailer_id: v === 'none' ? '' : v,
                            // picking a trailer clears unit so only one is set
                            unit_id: v === 'none' ? recordForm.unit_id : '',
                          })}
                        >
                          <SelectTrigger data-testid="record-trailer-select"><SelectValue placeholder="Select trailer" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— None —</SelectItem>
                            {trailers.map(t => <SelectItem key={t.id} value={t.id}>{t.trailer_number}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground -mt-2">Pick a Unit <em>or</em> a Trailer (one is required).</p>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label>Type *</Label>
                        <Select value={recordForm.record_type} onValueChange={(v) => setRecordForm({...recordForm, record_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {RECORD_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>
                                <div className="flex items-center gap-2">
                                  <t.icon className={`h-4 w-4 ${t.color}`} />
                                  {t.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input value={recordForm.title} onChange={(e) => setRecordForm({...recordForm, title: e.target.value})} placeholder="e.g., Annual DOT Inspection" required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date *</Label>
                        <Input type="date" value={recordForm.date} onChange={(e) => setRecordForm({...recordForm, date: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Cost</Label>
                        <Input type="number" step="0.01" value={recordForm.cost} onChange={(e) => setRecordForm({...recordForm, cost: e.target.value})} placeholder="0.00" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Vendor/Shop</Label>
                      <Input value={recordForm.vendor} onChange={(e) => setRecordForm({...recordForm, vendor: e.target.value})} placeholder="Service provider name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={recordForm.description} onChange={(e) => setRecordForm({...recordForm, description: e.target.value})} placeholder="Details..." rows={2} />
                    </div>
                    
                    {/* Expiration Date & Auto-Alert Section */}
                    <div className="border border-amber-200 dark:border-amber-800 rounded-lg p-4 bg-amber-50/50 dark:bg-amber-950/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Bell className="h-4 w-4 text-amber-600" />
                        <Label className="font-semibold text-amber-800 dark:text-amber-300">Expiration & Reminder</Label>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm">Expiration Date (optional)</Label>
                          <Input 
                            type="date" 
                            value={recordForm.expiration_date} 
                            onChange={(e) => setRecordForm({...recordForm, expiration_date: e.target.value})} 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Remind me</Label>
                          <Select 
                            value={recordForm.reminder_days} 
                            onValueChange={(v) => setRecordForm({...recordForm, reminder_days: v})}
                            disabled={!recordForm.expiration_date}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="7">7 days before</SelectItem>
                              <SelectItem value="14">14 days before</SelectItem>
                              <SelectItem value="30">30 days before</SelectItem>
                              <SelectItem value="45">45 days before</SelectItem>
                              <SelectItem value="60">60 days before</SelectItem>
                              <SelectItem value="90">90 days before</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {recordForm.expiration_date && !editingRecord && (
                        <div className="flex items-center gap-2 mt-3">
                          <input 
                            type="checkbox" 
                            id="create-alert" 
                            checked={recordForm.create_alert}
                            onChange={(e) => setRecordForm({...recordForm, create_alert: e.target.checked})}
                            className="rounded border-gray-300"
                          />
                          <label htmlFor="create-alert" className="text-sm text-amber-700 dark:text-amber-400">
                            Auto-create alert to remind me before expiration
                          </label>
                        </div>
                      )}
                    </div>
                    
                    {/* File Upload Section */}
                    <div className="space-y-2">
                      <Label>Attachments</Label>
                      <div className="border-2 border-dashed border-muted rounded-lg p-4">
                        {uploadedFiles.length === 0 ? (
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="h-8 w-8 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Upload documents (up to 100MB)</p>
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm" 
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}
                            >
                              {uploading ? 'Uploading...' : 'Choose File'}
                            </Button>
                            <input 
                              ref={fileInputRef} 
                              type="file" 
                              className="hidden" 
                              onChange={handleFileUpload}
                              accept="image/*,.pdf,.doc,.docx"
                            />
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {uploadedFiles.map((file) => (
                              <div key={file.file_id} className="flex items-center justify-between bg-muted/50 rounded-lg p-2">
                                <div className="flex items-center gap-2">
                                  <File className="h-4 w-4 text-blue-600" />
                                  <span className="text-sm truncate max-w-[200px]">{file.filename}</span>
                                </div>
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeUploadedFile(file.file_id)}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm" 
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}
                              className="w-full"
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              {uploading ? 'Uploading...' : 'Add Another File'}
                            </Button>
                            <input 
                              ref={fileInputRef} 
                              type="file" 
                              className="hidden" 
                              onChange={handleFileUpload}
                              accept="image/*,.pdf,.doc,.docx"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setRecordDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={uploading}>{editingRecord ? 'Update' : 'Add'} Record</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              </>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6"><Skeleton className="h-64 w-full" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unit / Trailer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Vendor</TableHead>
                      {canEdit && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No records found</p>
                        </TableCell>
                      </TableRow>
                    ) : records.map((record) => {
                      const typeInfo = getRecordTypeInfo(record.record_type);
                      return (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">{getAssetLabel(record)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <typeInfo.icon className={`h-4 w-4 ${typeInfo.color}`} />
                              {typeInfo.label}
                            </div>
                          </TableCell>
                          <TableCell>{record.title}</TableCell>
                          <TableCell>{formatDate(record.date)}</TableCell>
                          <TableCell>{record.cost ? formatCurrency(record.cost) : '-'}</TableCell>
                          <TableCell>{record.vendor || '-'}</TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" onClick={() => {
                                setEditingRecord(record);
                                setRecordForm({
                                  ...record,
                                  unit_id: record.unit_id || '',
                                  trailer_id: record.trailer_id || '',
                                  cost: record.cost?.toString() || '',
                                });
                                setRecordDialogOpen(true);
                              }}><Pencil className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteRecord(record.id)}><Trash2 className="h-4 w-4" /></Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <div className="flex justify-end">
            {canEdit && (
              <Dialog open={alertDialogOpen} onOpenChange={(open) => {
                setAlertDialogOpen(open);
                if (!open) resetAlertForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-alert-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Alert
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingAlert ? 'Edit' : 'Add'} Alert</DialogTitle>
                    <DialogDescription>Set reminders for important dates</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAlertSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Unit</Label>
                        <Select
                          value={alertForm.unit_id || 'none'}
                          onValueChange={(v) => setAlertForm({
                            ...alertForm,
                            unit_id: v === 'none' ? '' : v,
                            trailer_id: v === 'none' ? alertForm.trailer_id : '',
                          })}
                        >
                          <SelectTrigger data-testid="alert-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— None —</SelectItem>
                            <UnitSelectItems units={units} hideInactive />
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Trailer</Label>
                        <Select
                          value={alertForm.trailer_id || 'none'}
                          onValueChange={(v) => setAlertForm({
                            ...alertForm,
                            trailer_id: v === 'none' ? '' : v,
                            unit_id: v === 'none' ? alertForm.unit_id : '',
                          })}
                        >
                          <SelectTrigger data-testid="alert-trailer-select"><SelectValue placeholder="Select trailer" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— None —</SelectItem>
                            {trailers.map(t => <SelectItem key={t.id} value={t.id}>{t.trailer_number}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground -mt-2">Pick a Unit <em>or</em> a Trailer (one is required).</p>
                    <div className="space-y-2">
                      <Label>Type *</Label>
                        <Select value={alertForm.alert_type} onValueChange={(v) => setAlertForm({...alertForm, alert_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ALERT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input value={alertForm.title} onChange={(e) => setAlertForm({...alertForm, title: e.target.value})} placeholder="e.g., Annual DOT Inspection Due" required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Due Date *</Label>
                        <Input type="date" value={alertForm.due_date} onChange={(e) => setAlertForm({...alertForm, due_date: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select value={alertForm.status} onValueChange={(v) => setAlertForm({...alertForm, status: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea value={alertForm.notes} onChange={(e) => setAlertForm({...alertForm, notes: e.target.value})} rows={2} />
                    </div>
                    
                    {/* Reminder Settings */}
                    <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-950/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Bell className="h-4 w-4 text-blue-600" />
                        <Label className="font-semibold text-blue-800 dark:text-blue-300">Reminder Settings</Label>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm">Remind me</Label>
                          <Select value={alertForm.reminder_days} onValueChange={(v) => setAlertForm({...alertForm, reminder_days: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="7">7 days before</SelectItem>
                              <SelectItem value="14">14 days before</SelectItem>
                              <SelectItem value="30">30 days before</SelectItem>
                              <SelectItem value="45">45 days before</SelectItem>
                              <SelectItem value="60">60 days before</SelectItem>
                              <SelectItem value="90">90 days before</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2 flex items-end">
                          <div className="flex items-center gap-2">
                            <input 
                              type="checkbox" 
                              id="send-reminder" 
                              checked={alertForm.send_reminder}
                              onChange={(e) => setAlertForm({...alertForm, send_reminder: e.target.checked})}
                              className="rounded border-gray-300"
                            />
                            <label htmlFor="send-reminder" className="text-sm">Send email notification</label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setAlertDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{editingAlert ? 'Update' : 'Add'} Alert</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6"><Skeleton className="h-64 w-full" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unit / Trailer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      {canEdit && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No alerts set</p>
                        </TableCell>
                      </TableRow>
                    ) : alerts.map((alert) => (
                      <TableRow key={alert.id}>
                        <TableCell className="font-medium">{getAssetLabel(alert)}</TableCell>
                        <TableCell>{ALERT_TYPES.find(t => t.value === alert.alert_type)?.label || alert.alert_type}</TableCell>
                        <TableCell>{alert.title}</TableCell>
                        <TableCell>{formatDate(alert.due_date)}</TableCell>
                        <TableCell>{getStatusBadge(alert.status)}</TableCell>
                        {canEdit && (
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => {
                              setEditingAlert(alert);
                              setAlertForm({
                                ...alert,
                                unit_id: alert.unit_id || '',
                                trailer_id: alert.trailer_id || '',
                              });
                              setAlertDialogOpen(true);
                            }}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteAlert(alert.id)}><Trash2 className="h-4 w-4" /></Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
